/**
 * WS-HF — what a case job's `stage` MEANS, written once and kept pure.
 *
 * The owner's run control is the whole design: *"Only 2 AI stages normally: 1 clear + 1 magenta.
 * Local QC/processing occurs between them. Not a single automatic canvas run."* and
 * *"PASS → normalize. FAIL → identify defect; approve ONE retry of this stage. No automatic
 * retry loop."*
 *
 * So the two gates this module exists to hold are:
 *
 *  1. **A stage under review is a HUMAN decision.** Nothing here advances a stage on its own.
 *  2. **One approved retry, per stage.** Not "retry until it looks right" — every attempt is
 *     the operator's own credits, and a loop spends them without anybody choosing to.
 *
 * It is pure because these are the rules a card's buttons are derived from, and a rule that can
 * only be checked by rendering a component is a rule nobody re-checks.
 */
import type { CaseJobState, CaseJobStage, CaseJobView } from "@genz/platform";

/** The two AI stages — the server's own `CaseJobStage`. */
export type CaseStageKey = CaseJobStage;

/**
 * Where one stage is. `done` and `failed` are job-wide and belong to neither stage.
 *
 * `held` is the third outcome, and it is neither of the other two: the vendor ACCEPTED the
 * request and the answer was lost. The image may exist and may have been billed, so it is not a
 * failure; nothing is here to show, so it is not a success. It waits for a person to check.
 */
export type CasePhase = "pending" | "running" | "held" | "review" | "done" | "failed";

/**
 * Attempts one stage may make, INCLUDING the first.
 *
 * Two, not three: the first render, and the one retry the owner's check step approves.
 */
export const MAX_STAGE_ATTEMPTS = 2;

/** The two stages, in the order they run, with the words the wizard prints. */
export const CASE_STAGES: { key: CaseStageKey; label: string; hint: string }[] = [
  {
    key: "clear",
    label: "Stage 1 · Clear case",
    hint: "One clear-case packshot from the three photos. Check colour, hardware, cutout, tilt and crop.",
  },
  {
    key: "magenta",
    label: "Stage 2 · Print guide",
    hint: "The print guide, painted onto the approved clear base. Check the openings and the geometry.",
  },
];

/** Which stage the job is working on, or `null` once it is finished or has failed. */
export function currentStage(stage: CaseJobState): CaseStageKey | null {
  if (stage.startsWith("clear_")) return "clear";
  if (stage.startsWith("magenta_")) return "magenta";
  return null;
}

/** How far that stage has got. */
export function phaseOf(stage: CaseJobState): CasePhase {
  if (stage === "done") return "done";
  if (stage === "failed") return "failed";
  if (stage.endsWith("_pending")) return "pending";
  if (stage.endsWith("_running")) return "running";
  if (stage.endsWith("_held")) return "held";
  return "review";
}

/** Attempts a stage has made. A stage the server has not started yet has made none. */
export function attemptsOf(job: CaseJobView, key: CaseStageKey): number {
  return job.attempts?.[key] ?? 0;
}

/** Credits a stage has spent so far, across its attempts. */
export function creditsOf(job: CaseJobView, key: CaseStageKey): number {
  return job.credits?.[key] ?? 0;
}

/**
 * May THIS MACHINE generate the stage?
 *
 * Only a DEVICE job's pending stage: on the cloud path the worker starts every pending stage
 * itself, so there is nothing for a button to do and a "Generate" there would be a lie. A retry
 * on the device path lands the stage back in `pending` with an attempt behind it, and the
 * device must run that too — the attempt count is the latch key, not a gate.
 */
export function canGenerate(job: CaseJobView, key: CaseStageKey): boolean {
  return job.executor === "device"
    && currentStage(job.stage) === key
    && phaseOf(job.stage) === "pending";
}

export const POLL_MS = 3000;
export const HELD_POLL_MS = 15000;

/**
 * How often the card re-reads the job, or `null` for never.
 *
 * The cloud path only, while the WORKER owns the stage — `pending` and `running`, and an
 * auto-advancing job's stage-1 review, which the worker moves on past any check note. A held stage is re-read slowly: the cloud's recheck may still finish it. A device
 * stage is driven by this window, which already holds the answer.
 */
export function pollDelay(job: CaseJobView): number | null {
  if (job.executor !== "cloud") return null;
  const ph = phaseOf(job.stage);
  if (ph === "pending" || ph === "running") return POLL_MS;
  if (job.autoAdvance && job.stage === "clear_review") return POLL_MS;
  return ph === "held" ? HELD_POLL_MS : null;
}

/** May the operator accept what is on screen? Only a stage actually under review. */
export function canApprove(job: CaseJobView, key: CaseStageKey): boolean {
  return currentStage(job.stage) === key && phaseOf(job.stage) === "review";
}

/**
 * May the operator spend another generation on this stage?
 *
 * Exactly where the SERVER accepts `retry`: a stage under review or HELD, inside the attempt
 * budget. Held is included on purpose — a person may choose to pay again after checking the
 * request — and a failed job is not: it is terminal, and `retry` answers 409. Once the retry
 * is used, the card says so rather than offering a button that would be refused — a disabled
 * Retry with no sentence beside it is indistinguishable from a broken one.
 */
export function canRetry(job: CaseJobView, key: CaseStageKey): boolean {
  // The server says which retry it will accept (`retry_refusal`); only an older cloud without
  // the flag falls back to the rule below.
  if (job.retryable) return job.retryable[key];
  const ph = phaseOf(job.stage);
  const open = currentStage(job.stage) === key && (ph === "review" || ph === "held");
  return (open || isRefused(job, key)) && attemptsOf(job, key) < MAX_STAGE_ATTEMPTS;
}

/** The stage of a failed job: the later one that carries an error. */
export function failedStage(job: CaseJobView): CaseStageKey {
  return job.stageErrors?.magenta ? "magenta" : "clear";
}

/** A failed stage the vendor REFUSED — not charged, so the server accepts one retry (D2).
 *  An admin is sent the reason, a customer the code. */
function isRefused(job: CaseJobView, key: CaseStageKey): boolean {
  const error = job.stageErrors?.[key] ?? "";
  return phaseOf(job.stage) === "failed" && failedStage(job) === key
    && (error === "GEN-REFUSED" || /^higgsfield_(moderated|failed|canceled)/.test(error));
}

/** Retries left on this stage. Zero is the sentence "this stage's one retry is used". */
export function retriesLeft(job: CaseJobView, key: CaseStageKey): number {
  return Math.max(0, MAX_STAGE_ATTEMPTS - attemptsOf(job, key));
}

/**
 * The preview a stage shows under review.
 *
 * Stage 1 prefers the NORMALISED image when the server has one: that is the 1024², 95%-height
 * base stage 2 will actually be given, and approving the raw render while looking at a different
 * crop is approving something else.
 */
export function previewUrl(job: CaseJobView, key: CaseStageKey): string | undefined {
  if (key === "clear") return job.clearNormalizedUrl ?? job.clearResultUrl ?? undefined;
  return job.magentaResultUrl ?? undefined;
}

/**
 * Is there a generation in flight that the UI must WAIT for rather than start?
 *
 * This is the whole of the resume rule. A refresh mid-stage re-reads the job, sees `running`,
 * and polls — it does not generate, because the first generation is still running and the
 * operator would pay for both.
 */
export function isRunning(job: CaseJobView): boolean {
  return phaseOf(job.stage) === "running";
}

/**
 * Is this stage HELD — submitted, outcome unknown?
 *
 * A held stage offers no Generate, no Approve and no Retry. Every one of those would be wrong:
 * Generate and Retry could pay a second time for an image that may already exist, and Approve
 * would accept an image nobody has seen. The one honest action is to check again.
 */
export function isHeld(job: CaseJobView): boolean {
  return phaseOf(job.stage) === "held";
}

/** The vendor request id a held stage is checked by, when the server sent one. */
export function requestIdOf(job: CaseJobView, key: CaseStageKey): string | undefined {
  return job.requestIds?.[key] ?? undefined;
}

/**
 * The sentence for a stage that did not produce a usable image, or `undefined`.
 *
 * A failed job NEVER reads as a success anywhere. This is the one place the message is chosen,
 * and it prefers the stage's own error over the job's: "Higgsfield's content check refused this
 * image" is the recovery, and "the job failed" is not.
 */
export function failureText(job: CaseJobView): string | undefined {
  if (phaseOf(job.stage) !== "failed") return undefined;
  // The server writes the failing stage's own reason to `error`; `failedStage` names the stage.
  return job.error ?? "This stage did not produce an image.";
}

/**
 * Is the job finished, WITH the image the mask step needs?
 *
 * `done` without a `magentaResultUrl` is not a success: it is a job that says it finished and
 * has nothing to hand on. Treating the two the same is how an empty result reaches a print file.
 */
export function isComplete(job: CaseJobView): boolean {
  return job.stage === "done" && !!job.magentaResultUrl;
}
